import { SxProps, Theme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import LogoSmall from '@/components/LogoSmall';

const RootStyle = styled('div')(() => ({
  zIndex: 9,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

type Props = {
  sx?: SxProps<Theme>;
  text?: string;
};

function Loading({ text, ...other }: Props) {
  return (
    <RootStyle sx={{ p: 3 }} {...other}>
      <LogoSmall sx={{ width: 40, height: 40 }} />
      {text ? (
        <PTypography variant={'body1'} sx={{ ml: 2 }}>
          {text}
        </PTypography>
      ) : null}
    </RootStyle>
  );
}

type DelayedLoadingProps = {
  /**
   * time until the loading indicator is shown in milliseconds. Defaults to 1000.
   */
  delay?: number;
  /**
   * SX styles being passed to the div wrapping the loading SVG.
   */
  sx?: SxProps<Theme>;
  /**
   * if text needed to be shown with logo e.g loading, exporting etc
   */
  text?: string;
};

/**
 * Delaying is useful to not show janky loading indicators for short loading times.
 * Provide direct feedback to users (e.g. button with ripple effect)
 * and show loading on work that exceeds usually 1000ms.
 *
 * @returns Loading indicator after the delay or null
 */
export const DelayedLoading = ({ delay = 1000, sx, text }: DelayedLoadingProps) => {
  const [showLoading, setShowLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoading(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  return showLoading ? <Loading sx={sx} text={text} /> : null;
};
